import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Phone } from 'lucide-react';
import { useCurrentAccount, useMyAccounts } from './AccountContext';
import { RoleBadge } from '../components/RoleBadge';
import { cn } from '../lib/cn';

/** Which line you are in, and the way to leave it.
 *
 *  Two jobs in one control, and that pairing is deliberate: a separate "switch line"
 *  button somewhere else would be a second thing to find. The strip says where you
 *  are; pressing it offers the alternatives.
 *
 *  It is loud in someone else's line and quiet in your own. A bar that always sits
 *  there saying the same thing stops being read within days, and the day it matters is
 *  the day you are about to act on a call that is not yours. Being tinted only in the
 *  borrowed case makes the difference carry the meaning.
 *
 *  It hides completely for the ordinary customer — one line, and it is theirs — so the
 *  shell keeps its promise that screens own their own header. It appears when there is
 *  somewhere to switch TO, or when the line is not yours and you need telling. */
export function AccountSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = useCurrentAccount();
  const { accounts } = useMyAccounts();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const borrowed = current.myRole !== 'OWNER';
  const hasChoice = accounts.length > 1;

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close on navigation, so switching lines does not leave the panel hanging open
  // over the new screen.
  useEffect(() => setOpen(false), [pathname]);

  if (!borrowed && !hasChoice) return null;

  /** Switch lines without losing the screen: /a/<mine>/contacts → /a/<theirs>/contacts.
   *  Landing back on the call list every time would punish exactly the person this
   *  control exists for — someone comparing the same view across two lines. */
  function switchTo(accountId: string) {
    const rest = pathname.replace(/^\/a\/[^/]+/, '');
    navigate(`/a/${accountId}${rest}`);
  }

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          'flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs',
          borrowed
            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-b border-amber-500/30'
            : 'bg-muted/40 text-muted-foreground border-b border-border',
        )}
      >
        {borrowed ? <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
        <span className="truncate font-medium">{current.label ?? t('accounts.unnamed')}</span>
        {current.e164 ? <span className="hidden sm:inline opacity-70">{current.e164}</span> : null}
        <RoleBadge role={current.myRole} />
        {hasChoice ? (
          <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
        ) : null}
      </button>

      {open ? (
        <ul
          role="listbox"
          className="absolute inset-x-0 top-full z-30 max-h-[60vh] overflow-y-auto border-b border-border bg-card shadow-lg"
        >
          {accounts.map((a) => {
            const active = a.accountId === current.accountId;
            return (
              <li key={a.accountId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => switchTo(a.accountId)}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-muted"
                >
                  <Check
                    className={cn('h-4 w-4 shrink-0', active ? 'text-primary' : 'opacity-0')}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {a.label ?? t('accounts.unnamed')}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {a.e164 ?? t('accounts.noNumber')}
                    </span>
                  </span>
                  <RoleBadge role={a.myRole} />
                  {a.unread > 0 ? (
                    <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[11px] font-medium text-primary-foreground">
                      {a.unread}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
